import clsx from "clsx";
import type { GetStaticProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import config from "@/config";
import { fetchServerTranslations } from "@/i18n.server";
import type { GiftCardConfig } from "@/lib/giftcard/src/App";
import App from "@/lib/giftcard/src/App";
import { useTokenStore } from "@/lib/giftcard/src/lib/useToken";
import useSelection from "@/lib/hooks/useSelection";
import useStore from "@/lib/stateManagement/store";

import style from "./giftcard.module.css";

const GiftcardPage: NextPage = () => {
  const { location } = useSelection();
  const t = useTranslations();
  const router = useRouter();
  const mainToken = useStore((state) => state.token);

  const giftCardToken = useTokenStore((state) => state.token);
  if (mainToken && mainToken !== giftCardToken) {
    useTokenStore.setState({ token: mainToken });
  }

  const [config, setConfig] = useState<GiftCardConfig | null>({
    root: router.locale ? `/${router.locale}` : "",
    tokenKey: process.env.NEXT_PUBLIC_CENTRA_CUSTOMER_TOKEN as string,
    giftCardUrl: "/giftcard/success",
    centra: {
      url: process.env.NEXT_PUBLIC_CENTRA_CHECKOUT_API as string,
      country: location || "",
    },
    options: {
      noPlaceholders: true,
    },
  });

  const giftCardImage =
    router.locale !== "no"
      ? "/images/giftcard-no.png"
      : "/images/giftcard-en.png";

  useEffect(() => {
    if (location) {
      setConfig({
        root: router.locale ? `/${router.locale}` : "",
        tokenKey: process.env.NEXT_PUBLIC_CENTRA_CUSTOMER_TOKEN as string,
        giftCardUrl: "/giftcard/success",
        centra: {
          url: process.env.NEXT_PUBLIC_CENTRA_CHECKOUT_API as string,
          country: location,
        },
        options: {
          noPlaceholders: true,
        },
        configFields: {
          senderName: {
            placeholder: t("giftcardSender") || "",
            value: t("giftcardSender") || "",
            label: t("giftcardSender") || "",
          },
          message: {
            value: t("giftcardMessage") || "",
            label: t("giftcardMessage") || "",
            placeholder: t("giftcardMessage") || "",
          },
          giftCard: {
            value: "",
          },
          recieverName: {
            value: t("giftcardReceiver") || "",
            label: t("giftcardReceiver") || "",
            placeholder: t("giftcardReceiver") || "",
          },
          recieverEmail: {
            value: t("email") || "",
            label: t("email") || "",
            placeholder: t("email") || "",
            active: false,
          },
          amount: {
            value: "",
            label: "",
          },
          schedule: {
            active: false,
            labels: [],
          },
          button: t("continue") || "",
        },
        addressFields: {
          title: t("giftcardBillingInfo") || "",
          button: t("giftcardPurchaseButton") || "",
          phoneNumber: {
            value: "",
            label: t("phoneNumber") || "",
            placeholder: t("phoneNumber") || "",
          },
          firstName: {
            value: "",
            label: t("firstName") || "",
            placeholder: t("firstName") || "",
          },
          lastName: {
            value: "",
            label: t("lastName") || "",
            placeholder: t("lastName") || "",
          },
          email: {
            value: "",
            label: t("email") || "",
            placeholder: t("email") || "",
          },
          address1: {
            value: "",
            label: `${t("lastName")} 1` || "",
            placeholder: `${t("lastName")} 1` || "",
          },
          address2: {
            value: "",
            label: `${t("lastName")} 2` || "",
            placeholder: `${t("lastName")} 2` || "",
          },
          city: {
            value: "",
            label: t("city") || "",
            placeholder: t("city") || "",
          },
          zipCode: {
            value: "",
            label: t("zipcode") || "",
            placeholder: t("zipcode") || "",
          },
          country: {
            value: location || "",
            label: location || "",
            placeholder: location || "",
          },
          state: {
            value: location || "",
            label: location || "",
            placeholder: location || "",
          },
        },
      });
    }
  }, [location, router.locale, process.env.NEXT_PUBLIC_CENTRA_CHECKOUT_API]);

  if (router.isFallback) {
    return null;
  }

  if (!config) return null;

  return (
    <div className={clsx(style.giftCardPage, "giftcard-app-container")}>
      <div className={clsx(style.col, "giftcard-container-left")}>
        <img className={style.img} alt="Giftcard" src={giftCardImage} />
      </div>
      <div className={clsx(style.col, "giftcard-container-right")}>
        <main className={style.container}>
          <h3>{t("giftcardTitle")}</h3>
          <p>{t("giftcardBody")}</p>
          <App
            config={JSON.stringify(config)}
            route={"giftcard"}
            navigate={router.push}
          />
          <p style={{ marginTop: 20 }}>{t("giftcardFooter")}</p>
        </main>
      </div>
    </div>
  );
};
export const getStaticProps: GetStaticProps = async ({ locale = "en" }) => {
  return {
    props: {
      ...(await fetchServerTranslations(locale)),
    },
    revalidate: config.revalidate,
  };
};
export default GiftcardPage;
